import type { Theme } from '@mui/material';
import {
  Box,
  Button,
  Container,
  Link,
  Typography,
  useMediaQuery,
} from '@mui/material';
import React, { useMemo } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import StackedIcon from '@components/Icon/StackedIcon';
import ScanAppModalContent from '@components/Modal/Content/ScanAppModalContent';
import { ShareButton } from '@components/ShareButton';

import { useListScanResults } from '@hooks/api/scan';
import { useModal } from '@hooks/useModal';
import { useScanResults } from '@hooks/useScanResults';
import { useSession } from '@hooks/useSession';
import { useSnackbar } from '@hooks/useSnackbar';

import { getOS } from '@utils/get_os';
import { getScanAppLink, handleRenderQrCode } from '@utils/scan';

import { ScanType } from 'generated/types';

import Content from './Content';

const messages = defineMessages({
  newScan: {
    id: 'scanHistoryMain.newScan',
    defaultMessage: 'New {type} Scan',
  },
  disclaimer: {
    id: 'scanMetrics.disclaimer',
    defaultMessage:
      'The {type} scan contains estimations related to potential health risks. It is not intended to diagnose, treat, cure or prevent disease. Please consult your doctor for further interpretation of this report.',
  },
  error: {
    id: 'scanHistoryMain.error',
    defaultMessage:
      'An error occurred while trying to display more scan results.',
  },
});

const ScanHistoryMain = () => {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { openModal } = useModal();
  const { friendly_type, icons, scan_type, share_scan_type } = useScanResults();
  const isMobile = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md')
  );

  const {
    state: { activeProfile },
  } = useSession();
  const os = getOS();
  const usingMobileOrTablet = os === 'iOS' || os === 'Android';

  const vitall_patient_id = activeProfile?.vitall_patient_id;

  const { data: scanData, loading, fetchMore } = useListScanResults();
  const data = useMemo(() => scanData.data ?? [], [scanData.data]);

  const scanAppLink = useMemo(
    () => getScanAppLink(activeProfile?.email),
    [activeProfile?.email]
  );

  const handleOpenModal = () => {
    openModal({
      width: 400,
      props: {
        height: '300px',
      },
      children: <ScanAppModalContent />,
    });
  };

  const handleEnter = async () => {
    if (!vitall_patient_id || !scanData.cursor || !scanData.hasMore) {
      return;
    }
    try {
      await fetchMore({
        variables: {
          input: {
            vitall_patient_id,
            scan_type,
            cursor: scanData.cursor,
          },
          is_face_or_body:
            scan_type === ScanType.face || scan_type === ScanType.body,
          is_cough: scan_type === ScanType.respiratory,
          is_skin: scan_type === ScanType.skin,
        },
      });
    } catch (e) {
      enqueueSnackbar({
        message: formatMessage(messages.error),
        variant: 'error',
      });
    }
  };

  const hasData = Boolean(data.length);

  return (
    <Container sx={{ maxWidth: isMobile ? 500 : 'lg' }} disableGutters>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row-reverse',
          pb: 2,
          columnGap: 2,
        }}
      >
        {vitall_patient_id && (
          <ShareButton
            color="primary.main"
            share_type={share_scan_type}
            vitall_patient_id={vitall_patient_id}
          />
        )}
        {hasData && scanAppLink && (
          <>
            <Button
              color={scan_type === ScanType.body ? 'secondary' : 'primary'}
              component={usingMobileOrTablet ? Link : 'label'}
              disabled={loading}
              href={usingMobileOrTablet ? scanAppLink : undefined}
              onClick={() => {
                handleRenderQrCode(scanAppLink, handleOpenModal);
              }}
              startIcon={<StackedIcon icons={icons} />}
              variant="outlined"
            >
              {formatMessage(messages.newScan, {
                type: friendly_type,
              })}
            </Button>
            {os === 'iOS' && (
              <iframe
                title="VITALL Scan Redirect"
                id="ios-scan-app-redirect"
                width="1"
                height="1"
                style={{ visibility: 'hidden' }}
              />
            )}
          </>
        )}
      </Box>

      {hasData && (
        <Box
          sx={{
            pb: {
              xs: 2,
              md: 3,
            },
          }}
        >
          <Typography
            color="text.secondary"
            sx={{ textAlign: 'center' }}
            variant="body2"
          >
            {formatMessage(messages.disclaimer, {
              type: friendly_type.toLowerCase(),
            })}
          </Typography>
        </Box>
      )}

      <Content
        data={data}
        hasMore={scanData.hasMore}
        loading={loading}
        onEnter={handleEnter}
        patientId={activeProfile?.patient_id}
      />
    </Container>
  );
};

export default withErrorBoundary(ScanHistoryMain, {
  FallbackComponent: ErrorBoundaryFallback,
});
